import { type AutocompleteProps } from "@mui/material/Autocomplete";
import { useMemo } from "react";
import { useController, useFormContext } from "react-hook-form";
import { type SelectOption } from "@/components/common/formElements/types";
import { generateAutocompleteOptionsMapper } from "@/components/common/formElements/utils/generateAutocompleteOptionsLabelMapper";
import { transformValidationOptions } from "@/components/common/formElements/utils/transformValidationOptions";
import { useTranslation } from "@/i18n/client";
import type {
  FieldErrors,
  FieldValues,
  UseControllerProps,
} from "react-hook-form";

export type AutocompleteOption = SelectOption;

export interface UseAutocompleteFieldControllerProps<
  TValue extends AutocompleteOption,
  TFieldValues extends FieldValues = FieldValues,
> extends UseControllerProps<TFieldValues> {
  options: TValue[];
  onChange?: AutocompleteProps<TValue, boolean, boolean, boolean>["onChange"];
}

export function useAutocompleteFieldController<
  TValue extends AutocompleteOption,
  TFieldValues extends FieldValues,
>(props: UseAutocompleteFieldControllerProps<TValue, TFieldValues>) {
  const { name, rules, onChange, options, ...autocompleteControllerProps } =
    props;

  const { t } = useTranslation("form-elements");
  const transformedRules = transformValidationOptions<TFieldValues>(t, rules);
  const optionsLabelMapper = useMemo(() => {
    return generateAutocompleteOptionsMapper(options, "value", "label");
  }, [options]);

  const required = typeof transformedRules.required !== "undefined";

  const { control } = useFormContext<TFieldValues>();
  const controllerProps = useController<TFieldValues>({
    control,
    name,
    rules: transformedRules,
    ...autocompleteControllerProps,
  });

  const { field, formState } = controllerProps;

  const handleChange: AutocompleteProps<
    TValue,
    boolean,
    boolean,
    boolean
  >["onChange"] = (event, value, reason, details) => {
    if (Array.isArray(value)) {
      field.onChange(
        value.map((item) => (typeof item === "string" ? item : item.value)),
      );
    } else {
      const newValue = typeof value === "object" ? value?.value : value;

      field.onChange(newValue || "");
    }

    onChange?.(event, value, reason, details);
  };

  const { invalid: error } = controllerProps.fieldState;

  const fieldError = formState.errors[field.name];
  const errors = {
    [field.name]: Array.isArray(fieldError)
      ? fieldError.find((error) => error)
      : fieldError,
  };

  return {
    ...controllerProps,
    field,
    onChangeField: handleChange,
    required,
    error,
    errors: errors as FieldErrors<TFieldValues>,
    optionsLabelMapper,
  };
}
